/**
 * Jurisdiction-neutral governed content contracts.
 *
 * This module deliberately contains data shapes and structural validation only.
 * It neither resolves organization access nor executes policy code.
 */
/** Data-only rule representation. Packages cannot embed code, queries, imports, or routes. */
export const DECLARATIVE_POLICY_OPERATORS = Object.freeze([
    'EQ',
    'NEQ',
    'IN',
    'GT',
    'GTE',
    'LT',
    'LTE',
    'EXISTS',
    'ALL',
    'ANY',
    'NOT',
]);
export const DECLARATIVE_POLICY_ACTIONS = Object.freeze([
    'ALLOW',
    'DENY',
    'REQUIRE_REVIEW',
    'FLAG',
    'CLASSIFY',
    'SET_VALUE',
]);
export function validateDeclarativePolicyRules(value) {
    const errors = [];
    const validateCondition = (condition, path, depth) => {
        if (depth > MAX_OBJECT_DEPTH) {
            errors.push(`${path} exceeds maximum depth`);
            return;
        }
        if (!hasOnlyKeys(condition, ['operator', 'fact', 'value', 'children'], path, errors))
            return;
        if (!DECLARATIVE_POLICY_OPERATORS.includes(condition.operator))
            errors.push(`${path}.operator is invalid`);
        if (typeof condition.fact !== 'undefined' &&
            (typeof condition.fact !== 'string' ||
                !/^[A-Za-z][A-Za-z0-9_.-]{0,127}$/.test(condition.fact)))
            errors.push(`${path}.fact is invalid`);
        if (condition.children !== undefined) {
            if (!Array.isArray(condition.children) ||
                condition.children.length === 0 ||
                condition.children.length > MAX_ARRAY_LENGTH)
                errors.push(`${path}.children is invalid`);
            else
                condition.children.forEach((child, index) => validateCondition(child, `${path}.children[${index}]`, depth + 1));
        }
        validateBoundedJson(condition.value, `${path}.value`, errors, depth + 1);
    };
    if (!Array.isArray(value) || value.length > MAX_ARRAY_LENGTH)
        return ['rules must be a bounded array'];
    value.forEach((rule, index) => {
        if (!hasOnlyKeys(rule, ['ruleId', 'when', 'effects'], `rules[${index}]`, errors))
            return;
        requireString(rule.ruleId, `rules[${index}].ruleId`, errors);
        validateCondition(rule.when, `rules[${index}].when`, 0);
        if (!Array.isArray(rule.effects) ||
            !rule.effects.length ||
            rule.effects.length > MAX_ARRAY_LENGTH) {
            errors.push(`rules[${index}].effects is invalid`);
            return;
        }
        rule.effects.forEach((effect, effectIndex) => {
            if (!hasOnlyKeys(effect, ['action', 'target', 'value'], `rules[${index}].effects[${effectIndex}]`, errors))
                return;
            if (!DECLARATIVE_POLICY_ACTIONS.includes(effect.action))
                errors.push(`rules[${index}].effects[${effectIndex}].action is invalid`);
            requireString(effect.target, `rules[${index}].effects[${effectIndex}].target`, errors);
            validateBoundedJson(effect.value, `rules[${index}].effects[${effectIndex}].value`, errors);
        });
    });
    return errors;
}
/**
 * Registry ownership is contract-level. Evaluator implementation remains in an
 * owning domain module and is selected by this stable allowlist only.
 */
export const FINANCIAL_POLICY_FAMILY_REGISTRY = Object.freeze([
    {
        familyId: 'CURRENCY',
        schemaVersion: '1',
        evaluatorId: 'reference.currency.v1',
        payloadSchemaId: 'financial.currency.v1',
        description: 'Currency reference data.',
        governed: false,
    },
    {
        familyId: 'EXCHANGE',
        schemaVersion: '1',
        evaluatorId: 'reference.exchange.v1',
        payloadSchemaId: 'financial.exchange.v1',
        description: 'Exchange reference data.',
        governed: false,
    },
    {
        familyId: 'CALENDAR',
        schemaVersion: '1',
        evaluatorId: 'calendar.definition.v1',
        payloadSchemaId: 'financial.calendar.v1',
        description: 'Calendar and period inputs.',
        governed: true,
    },
    {
        familyId: 'HOLIDAY',
        schemaVersion: '1',
        evaluatorId: 'reference.holiday.v1',
        payloadSchemaId: 'financial.holiday.v1',
        description: 'Holiday reference data.',
        governed: false,
    },
    {
        familyId: 'FX_PAIR',
        schemaVersion: '1',
        evaluatorId: 'reference.fx-pair.v1',
        payloadSchemaId: 'financial.fx-pair.v1',
        description: 'FX pair reference data.',
        governed: false,
    },
    {
        familyId: 'BENCHMARK',
        schemaVersion: '1',
        evaluatorId: 'reference.benchmark.v1',
        payloadSchemaId: 'financial.benchmark.v1',
        description: 'Benchmark reference data.',
        governed: false,
    },
    {
        familyId: 'FIXED_INCOME_TERMS',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.terms.v1',
        payloadSchemaId: 'financial.fixed-income-terms.v1',
        description: 'Fixed-income terms.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_CALENDAR',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.calendar.v1',
        payloadSchemaId: 'financial.fixed-income-calendar.v1',
        description: 'Fixed-income calendar.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_ADJUSTMENT_STRATEGY',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.adjustment-strategy.v1',
        payloadSchemaId: 'financial.fixed-income-adjustment-strategy.v1',
        description: 'Fixed-income adjustment strategy.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_CONVENTION',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.convention.v1',
        payloadSchemaId: 'financial.fixed-income-convention.v1',
        description: 'Fixed-income convention.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_HOLIDAY_CALENDAR',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.holiday-calendar.v1',
        payloadSchemaId: 'financial.fixed-income-holiday-calendar.v1',
        description: 'Fixed-income holiday calendar.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_LIFECYCLE',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.lifecycle.v1',
        payloadSchemaId: 'financial.fixed-income-lifecycle.v1',
        description: 'Fixed-income lifecycle.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_AMORTIZATION',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.amortization.v1',
        payloadSchemaId: 'financial.fixed-income-amortization.v1',
        description: 'Fixed-income amortization.',
        governed: true,
    },
    {
        familyId: 'FIXED_INCOME_RATE_OBSERVATION',
        schemaVersion: '1',
        evaluatorId: 'fixed-income.rate-observation.v1',
        payloadSchemaId: 'financial.fixed-income-rate-observation.v1',
        description: 'Fixed-income rate observation.',
        governed: true,
    },
    {
        familyId: 'CORPORATE_ACTION_EX_DATE',
        schemaVersion: '1',
        evaluatorId: 'corporate-action.ex-date.v1',
        payloadSchemaId: 'financial.corporate-action-ex-date.v1',
        description: 'Corporate-action ex-date policy.',
        governed: true,
    },
    {
        familyId: 'FINANCIAL_CONTROL_RECONCILIATION',
        schemaVersion: '1',
        evaluatorId: 'financial-control.reconciliation.v1',
        payloadSchemaId: 'financial.financial-control-reconciliation.v1',
        description: 'Financial control reconciliation policy.',
        governed: true,
    },
    {
        familyId: 'RECONCILIATION_POLICY',
        schemaVersion: '1',
        evaluatorId: 'reconciliation.policy.v1',
        payloadSchemaId: 'financial.reconciliation-policy.v1',
        description: 'Reconciliation policy.',
        governed: true,
    },
    {
        familyId: 'REGULATORY_REPORTING_DEADLINE',
        schemaVersion: '1',
        evaluatorId: 'regulatory-reporting.deadline.v1',
        payloadSchemaId: 'financial.regulatory-reporting-deadline.v1',
        description: 'Regulatory reporting deadline.',
        governed: true,
    },
    {
        familyId: 'ACCOUNTING_CLASSIFICATION',
        schemaVersion: '1',
        evaluatorId: 'accounting.classification.v1',
        payloadSchemaId: 'financial.accounting-classification.v1',
        description: 'Accounting classification inputs.',
        governed: true,
    },
    {
        familyId: 'VALUATION_OVERRIDE_POLICY',
        schemaVersion: '1',
        evaluatorId: 'valuation.override-policy.v1',
        payloadSchemaId: 'financial.valuation-override-policy.v1',
        description: 'Valuation override approval policy.',
        governed: true,
    },
    {
        familyId: 'EXPECTED_CREDIT_LOSS',
        schemaVersion: '1',
        evaluatorId: 'accounting.ecl.v1',
        payloadSchemaId: 'financial.expected-credit-loss.v1',
        description: 'Expected credit loss model inputs.',
        governed: true,
    },
    {
        familyId: 'FAIR_VALUE_MEASUREMENT',
        schemaVersion: '1',
        evaluatorId: 'accounting.fair-value.v1',
        payloadSchemaId: 'financial.fair-value.v1',
        description: 'Fair value measurement inputs.',
        governed: true,
    },
    {
        familyId: 'ACCOUNTING_DISCLOSURE',
        schemaVersion: '1',
        evaluatorId: 'accounting.disclosure.v1',
        payloadSchemaId: 'financial.accounting-disclosure.v1',
        description: 'Accounting disclosure inputs.',
        governed: true,
    },
    {
        familyId: 'TAX_CALCULATION',
        schemaVersion: '1',
        evaluatorId: 'tax.calculation.v1',
        payloadSchemaId: 'financial.tax-calculation.v1',
        description: 'Tax calculation inputs.',
        governed: true,
    },
    {
        familyId: 'MARKET_CONVENTION',
        schemaVersion: '1',
        evaluatorId: 'market.convention.v1',
        payloadSchemaId: 'financial.market-convention.v1',
        description: 'Market convention inputs.',
        governed: true,
    },
    {
        familyId: 'IDENTIFIER_PRIVACY',
        schemaVersion: '1',
        evaluatorId: 'privacy.identifier.v1',
        payloadSchemaId: 'financial.identifier-privacy.v1',
        description: 'Identifier handling inputs.',
        governed: true,
    },
    {
        familyId: 'REPORTING',
        schemaVersion: '1',
        evaluatorId: 'reporting.definition.v1',
        payloadSchemaId: 'financial.reporting.v1',
        description: 'Reporting definition inputs.',
        governed: true,
    },
    {
        familyId: 'AML',
        schemaVersion: '1',
        evaluatorId: 'aml.case.v1',
        payloadSchemaId: 'financial.aml.v1',
        description: 'AML policy inputs.',
        governed: true,
    },
    {
        familyId: 'RELATED_PARTY_CONFLICT',
        schemaVersion: '1',
        evaluatorId: 'compliance.related-party-conflict.v1',
        payloadSchemaId: 'financial.related-party-conflict.v1',
        description: 'Related-party and conflict governance.',
        governed: true,
    },
    {
        familyId: 'CONTINUOUS_SCREENING',
        schemaVersion: '1',
        evaluatorId: 'compliance.continuous-screening.v1',
        payloadSchemaId: 'financial.continuous-screening.v1',
        description: 'Sanctions and PEP screening governance.',
        governed: true,
    },
    {
        familyId: 'REGULATORY_CASE_REPORTING',
        schemaVersion: '1',
        evaluatorId: 'compliance.regulatory-case-reporting.v1',
        payloadSchemaId: 'financial.regulatory-case-reporting.v1',
        description: 'Confidential regulatory case and report governance.',
        governed: true,
    },
    {
        familyId: 'CLIENT_AGREEMENT_REQUIREMENTS',
        schemaVersion: '1',
        evaluatorId: 'client.agreement-requirements.v1',
        payloadSchemaId: 'financial.client-agreement-requirements.v1',
        description: 'Client agreement requirements.',
        governed: true,
    },
    {
        familyId: 'CLIENT_STATEMENT_POLICY',
        schemaVersion: '1',
        evaluatorId: 'client.statement-policy.v1',
        payloadSchemaId: 'financial.client-statement-policy.v1',
        description: 'Client statement policy.',
        governed: true,
    },
    {
        familyId: 'COMPLAINT_GOVERNANCE',
        schemaVersion: '1',
        evaluatorId: 'client.complaint-governance.v1',
        payloadSchemaId: 'financial.complaint-governance.v1',
        description: 'Complaint governance policy.',
        governed: true,
    },
    {
        familyId: 'CUSTODY_IDENTIFIER_POLICY',
        schemaVersion: '1',
        evaluatorId: 'custody.identifier-format.v1',
        payloadSchemaId: 'financial.custody-identifier-policy.v1',
        description: 'Custody-account identifier formats, pairing, and masking policy.',
        governed: true,
    },
    {
        familyId: 'PRIVACY_RIGHTS',
        schemaVersion: '1',
        evaluatorId: 'privacy.rights.v1',
        payloadSchemaId: 'financial.privacy-rights.v1',
        description: 'Privacy request, restriction, correction, consent, and retention governance.',
        governed: true,
    },
    {
        familyId: 'INTEGRATION_MESSAGE_POLICY',
        schemaVersion: '1',
        evaluatorId: 'integration.message-authentication.v1',
        payloadSchemaId: 'financial.integration-message-policy.v1',
        description: 'Signed integration message, replay, sequencing, and rotation governance.',
        governed: true,
    },
    {
        familyId: 'MONETARY_APPROVAL_POLICY',
        schemaVersion: '1',
        evaluatorId: 'approval.monetary-limit.v1',
        payloadSchemaId: 'financial.monetary-approval-policy.v1',
        description: 'Amount, action, scope, FX, splitting, and delegation approval governance.',
        governed: true,
    },
    {
        familyId: 'RISK_ANALYTICS_POLICY',
        schemaVersion: '1',
        evaluatorId: 'risk.analytics.v1',
        payloadSchemaId: 'financial.risk-analytics-policy.v1',
        description: 'Exposure, liquidity, stress, limit, and completeness governance.',
        governed: true,
    },
]);
const PROTOTYPE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const MAX_STRING_LENGTH = 4_096;
const MAX_ARRAY_LENGTH = 1_024;
const MAX_OBJECT_DEPTH = 16;
function isPlainObject(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function isIsoDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
function isIsoInstant(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value))
        return false;
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.toISOString() === value;
}
function hasOnlyKeys(value, keys, path, errors) {
    if (!isPlainObject(value)) {
        errors.push(`${path} must be a plain object`);
        return false;
    }
    for (const key of Object.keys(value)) {
        if (PROTOTYPE_KEYS.has(key))
            errors.push(`${path}.${key} is forbidden`);
        else if (!keys.includes(key))
            errors.push(`${path}.${key} is not allowed`);
    }
    return true;
}
function validateBoundedJson(value, path, errors, depth = 0) {
    if (value === undefined)
        return;
    if (depth > MAX_OBJECT_DEPTH) {
        errors.push(`${path} exceeds maximum depth`);
        return;
    }
    if (typeof value === 'string' && value.length > MAX_STRING_LENGTH)
        errors.push(`${path} exceeds maximum string length`);
    if (typeof value === 'number' && !Number.isFinite(value))
        errors.push(`${path} must be finite`);
    if (Array.isArray(value)) {
        if (value.length > MAX_ARRAY_LENGTH)
            errors.push(`${path} exceeds maximum array length`);
        value.forEach((entry, index) => validateBoundedJson(entry, `${path}[${index}]`, errors, depth + 1));
    }
    else if (isPlainObject(value)) {
        for (const [key, entry] of Object.entries(value)) {
            if (PROTOTYPE_KEYS.has(key))
                errors.push(`${path}.${key} is forbidden`);
            validateBoundedJson(entry, `${path}.${key}`, errors, depth + 1);
        }
    }
    else if (value !== null && !['string', 'number', 'boolean'].includes(typeof value))
        errors.push(`${path} has unsupported value type`);
}
function uniqueNonEmpty(values, path, errors) {
    if (values.length > MAX_ARRAY_LENGTH)
        errors.push(`${path} exceeds maximum array length`);
    if (new Set(values).size !== values.length)
        errors.push(`${path} contains duplicate values`);
    values.forEach((value, index) => {
        if (!value.trim())
            errors.push(`${path}[${index}] is required`);
    });
}
function requireString(value, path, errors) {
    if (typeof value !== 'string' || !value.trim()) {
        errors.push(`${path} is required`);
        return false;
    }
    if (value.length > MAX_STRING_LENGTH)
        errors.push(`${path} exceeds maximum string length`);
    return true;
}
export function canonicalizeGovernedContent(value) {
    const visit = (entry, path, seen) => {
        if (entry === null)
            return 'null';
        if (typeof entry === 'string')
            return JSON.stringify(entry);
        if (typeof entry === 'boolean')
            return entry ? 'true' : 'false';
        if (typeof entry === 'number') {
            if (!Number.isFinite(entry))
                throw new TypeError(`${path} must be finite`);
            return JSON.stringify(entry);
        }
        if (Array.isArray(entry))
            return `[${entry.map((item, index) => visit(item, `${path}[${index}]`, seen)).join(',')}]`;
        if (!isPlainObject(entry))
            throw new TypeError(`${path} must be a JSON value`);
        if (seen.has(entry))
            throw new TypeError(`${path} contains a cycle`);
        seen.add(entry);
        const result = `{${Object.keys(entry)
            .sort()
            .map((key) => {
            if (PROTOTYPE_KEYS.has(key))
                throw new TypeError(`${path}.${key} is forbidden`);
            return `${JSON.stringify(key)}:${visit(entry[key], `${path}.${key}`, seen)}`;
        })
            .join(',')}}`;
        seen.delete(entry);
        return result;
    };
    return visit(value, '$', new WeakSet());
}
export function canonicalGovernedContentBytes(value) {
    return new TextEncoder().encode(canonicalizeGovernedContent(value));
}
export function validateJurisdictionContentPackageDescriptor(value) {
    const errors = [];
    const keys = [
        'schemaVersion',
        'packageId',
        'jurisdictionId',
        'version',
        'platformContractRange',
        'lifecycleStatus',
        'contentStatus',
        'effectiveFrom',
        'effectiveTo',
        'regulatedEntityTypes',
        'currencies',
        'languages',
        'modules',
        'sourceAuthorities',
        'sourceDocuments',
        'interpretationReviews',
        'contentVersions',
        'policyBindings',
        'manifestHash',
        'supersedes',
    ];
    if (!hasOnlyKeys(value, keys, 'package', errors))
        return errors;
    for (const key of [
        'schemaVersion',
        'packageId',
        'jurisdictionId',
        'version',
        'platformContractRange',
        'lifecycleStatus',
        'contentStatus',
        'effectiveFrom',
    ])
        requireString(value[key], `package.${key}`, errors);
    if (value.schemaVersion !== 'jurisdiction-content-package.v1')
        errors.push('package.schemaVersion is unsupported');
    if (!isIsoDate(value.effectiveFrom))
        errors.push('package.effectiveFrom must be an ISO date');
    if (value.effectiveTo !== undefined && !isIsoDate(value.effectiveTo))
        errors.push('package.effectiveTo must be an ISO date');
    if (isIsoDate(value.effectiveFrom) &&
        isIsoDate(value.effectiveTo) &&
        value.effectiveTo < value.effectiveFrom)
        errors.push('package.effectiveTo precedes package.effectiveFrom');
    if (!['DRAFT', 'SUBMITTED', 'APPROVED', 'ACTIVE', 'SUPERSEDED', 'RETIRED'].includes(value.lifecycleStatus))
        errors.push('package.lifecycleStatus is invalid');
    if (!['UNVERIFIED', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'TEST_ONLY'].includes(value.contentStatus))
        errors.push('package.contentStatus is invalid');
    for (const key of ['regulatedEntityTypes', 'currencies', 'languages']) {
        if (!Array.isArray(value[key]) || !value[key].every((entry) => typeof entry === 'string'))
            errors.push(`package.${key} must be a string array`);
        else
            uniqueNonEmpty(value[key], `package.${key}`, errors);
    }
    for (const key of [
        'modules',
        'sourceAuthorities',
        'sourceDocuments',
        'interpretationReviews',
        'contentVersions',
    ]) {
        if (!Array.isArray(value[key]))
            errors.push(`package.${key} must be an array`);
        else if (value[key].length > MAX_ARRAY_LENGTH)
            errors.push(`package.${key} exceeds maximum array length`);
    }
    const authorities = new Set();
    (Array.isArray(value.sourceAuthorities) ? value.sourceAuthorities : []).forEach((authority, index) => {
        if (!hasOnlyKeys(authority, ['id', 'displayName', 'authorityType', 'officialPublicationRequired', 'canonicalBaseUrl'], `package.sourceAuthorities[${index}]`, errors))
            return;
        requireString(authority.id, `package.sourceAuthorities[${index}].id`, errors);
        requireString(authority.displayName, `package.sourceAuthorities[${index}].displayName`, errors);
        requireString(authority.authorityType, `package.sourceAuthorities[${index}].authorityType`, errors);
        if (typeof authority.officialPublicationRequired !== 'boolean')
            errors.push(`package.sourceAuthorities[${index}].officialPublicationRequired must be boolean`);
        if (typeof authority.id === 'string') {
            if (authorities.has(authority.id))
                errors.push(`package.sourceAuthorities contains duplicate ${authority.id}`);
            authorities.add(authority.id);
        }
    });
    const documents = new Set();
    (Array.isArray(value.sourceDocuments) ? value.sourceDocuments : []).forEach((document, index) => {
        if (!hasOnlyKeys(document, [
            'documentId',
            'authorityId',
            'canonicalUrl',
            'publicationIdentity',
            'publicationDate',
            'retrievedAt',
            'language',
            'contentHash',
            'relevantLocations',
            'reviewStatus',
        ], `package.sourceDocuments[${index}]`, errors))
            return;
        for (const key of [
            'documentId',
            'authorityId',
            'canonicalUrl',
            'publicationIdentity',
            'publicationDate',
            'retrievedAt',
            'language',
            'contentHash',
            'reviewStatus',
        ])
            requireString(document[key], `package.sourceDocuments[${index}].${key}`, errors);
        if (!authorities.has(document.authorityId))
            errors.push(`package.sourceDocuments[${index}].authorityId is unknown`);
        if (!isIsoDate(document.publicationDate))
            errors.push(`package.sourceDocuments[${index}].publicationDate must be an ISO date`);
        if (!isIsoInstant(document.retrievedAt))
            errors.push(`package.sourceDocuments[${index}].retrievedAt must be an ISO instant`);
        if (!Array.isArray(document.relevantLocations) ||
            !document.relevantLocations.every((entry) => typeof entry === 'string'))
            errors.push(`package.sourceDocuments[${index}].relevantLocations must be a string array`);
        if (document.reviewStatus === 'APPROVED' &&
            (!Array.isArray(document.relevantLocations) || document.relevantLocations.length === 0))
            errors.push(`package.sourceDocuments[${index}].relevantLocations is required for approved content`);
        if (typeof document.documentId === 'string') {
            if (documents.has(document.documentId))
                errors.push(`package.sourceDocuments contains duplicate ${document.documentId}`);
            documents.add(document.documentId);
        }
    });
    const families = new Map(FINANCIAL_POLICY_FAMILY_REGISTRY.map((family) => [family.familyId, family]));
    if (!Array.isArray(value.policyBindings))
        errors.push('package.policyBindings must be an array');
    else {
        const boundFamilies = new Set();
        value.policyBindings.forEach((binding, index) => {
            if (!hasOnlyKeys(binding, ['familyId', 'referenceKey'], `package.policyBindings[${index}]`, errors))
                return;
            requireString(binding.familyId, `package.policyBindings[${index}].familyId`, errors);
            requireString(binding.referenceKey, `package.policyBindings[${index}].referenceKey`, errors);
            const family = families.get(binding.familyId);
            if (!family)
                errors.push(`package.policyBindings[${index}].familyId is unknown`);
            else if (!family.governed)
                errors.push(`package.policyBindings[${index}].familyId is not governed`);
            if (typeof binding.familyId === 'string' && !/^[A-Z0-9_]{1,128}$/.test(binding.familyId))
                errors.push(`package.policyBindings[${index}].familyId must be canonical uppercase`);
            if (typeof binding.referenceKey === 'string' &&
                !/^[A-Z0-9_./:-]{1,256}$/.test(binding.referenceKey))
                errors.push(`package.policyBindings[${index}].referenceKey must be canonical uppercase`);
            if (boundFamilies.has(binding.familyId))
                errors.push(`package.policyBindings contains duplicate ${binding.familyId}`);
            boundFamilies.add(binding.familyId);
        });
    }
    const policies = new Set();
    (Array.isArray(value.contentVersions) ? value.contentVersions : []).forEach((policy, index) => {
        if (!hasOnlyKeys(policy, [
            'policyId',
            'policyVersion',
            'familyId',
            'schemaVersion',
            'evaluatorId',
            'packageId',
            'effectiveFrom',
            'effectiveTo',
            'supersedes',
            'sourceDocumentIds',
            'interpretationReviewIds',
            'contentHash',
            'implementationStatus',
            'payload',
        ], `package.contentVersions[${index}]`, errors))
            return;
        for (const key of [
            'policyId',
            'policyVersion',
            'familyId',
            'schemaVersion',
            'evaluatorId',
            'packageId',
            'effectiveFrom',
            'contentHash',
            'implementationStatus',
        ])
            requireString(policy[key], `package.contentVersions[${index}].${key}`, errors);
        const family = families.get(policy.familyId);
        if (!family)
            errors.push(`package.contentVersions[${index}].familyId is unknown`);
        else if (policy.schemaVersion !== family.schemaVersion ||
            policy.evaluatorId !== family.evaluatorId)
            errors.push(`package.contentVersions[${index}] does not match its family registry`);
        if (policy.packageId !== value.packageId)
            errors.push(`package.contentVersions[${index}].packageId does not match package`);
        if (!isIsoDate(policy.effectiveFrom) ||
            (policy.effectiveTo !== undefined && !isIsoDate(policy.effectiveTo)))
            errors.push(`package.contentVersions[${index}] has invalid effective date`);
        if (isIsoDate(policy.effectiveFrom) &&
            isIsoDate(policy.effectiveTo) &&
            policy.effectiveTo < policy.effectiveFrom)
            errors.push(`package.contentVersions[${index}].effectiveTo precedes effectiveFrom`);
        if (!Array.isArray(policy.sourceDocumentIds) ||
            !policy.sourceDocumentIds.every((entry) => typeof entry === 'string'))
            errors.push(`package.contentVersions[${index}].sourceDocumentIds must be a string array`);
        else
            for (const id of policy.sourceDocumentIds)
                if (!documents.has(id))
                    errors.push(`package.contentVersions[${index}] references unknown document ${id}`);
        if (!Array.isArray(policy.interpretationReviewIds) ||
            !policy.interpretationReviewIds.every((entry) => typeof entry === 'string'))
            errors.push(`package.contentVersions[${index}].interpretationReviewIds must be a string array`);
        validateBoundedJson(policy.payload, `package.contentVersions[${index}].payload`, errors);
        const key = `${policy.policyId}@${policy.policyVersion}`;
        if (policies.has(key))
            errors.push(`package.contentVersions contains duplicate ${key}`);
        policies.add(key);
    });
    (Array.isArray(value.modules) ? value.modules : []).forEach((module, index) => {
        if (!hasOnlyKeys(module, [
            'id',
            'enabledByDefault',
            'regulatedEntityTypes',
            'resourceIds',
            'reportIds',
            'policyIds',
            'sourceAuthorityIds',
            'dependencies',
        ], `package.modules[${index}]`, errors))
            return;
        requireString(module.id, `package.modules[${index}].id`, errors);
        if (typeof module.enabledByDefault !== 'boolean')
            errors.push(`package.modules[${index}].enabledByDefault must be boolean`);
        for (const key of ['resourceIds', 'reportIds', 'policyIds', 'sourceAuthorityIds']) {
            if (!Array.isArray(module[key]) || !module[key].every((entry) => typeof entry === 'string'))
                errors.push(`package.modules[${index}].${key} must be a string array`);
        }
        for (const id of Array.isArray(module.sourceAuthorityIds) ? module.sourceAuthorityIds : [])
            if (!authorities.has(id))
                errors.push(`package.modules[${index}] references unknown authority ${id}`);
    });
    if (value.contentStatus === 'APPROVED' &&
        (!Array.isArray(value.sourceDocuments) || value.sourceDocuments.length === 0))
        errors.push('package.sourceDocuments are required for approved content');
    return errors;
}
export function validatePolicyResolutionRequest(value) {
    const errors = [];
    if (!hasOnlyKeys(value, ['organizationId', 'regulatedEntityType', 'eventTime', 'requiredFamilyIds'], 'request', errors))
        return errors;
    for (const key of ['organizationId', 'regulatedEntityType', 'eventTime'])
        requireString(value[key], `request.${key}`, errors);
    if (!isIsoInstant(value.eventTime))
        errors.push('request.eventTime must be an ISO instant');
    if (!Array.isArray(value.requiredFamilyIds) ||
        !value.requiredFamilyIds.every((entry) => typeof entry === 'string'))
        errors.push('request.requiredFamilyIds must be a string array');
    else
        uniqueNonEmpty(value.requiredFamilyIds, 'request.requiredFamilyIds', errors);
    return errors;
}
